function safeRandomUUID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const MATTER_TYPES = ["Criminal", "Family", "Family Violence", "Civil", "Other"];
export const APPLICATION_STATUS_OPTIONS = [
  "Approved",
  "Pending",
  "Refused",
  "Requisition to be answered",
  "Approved with a caveat",
  "Approved with a contribution",
];

export const COURTS = [
  "Magistrates' Court",
  "County Court",
  "Supreme Court",
  "Children's Court",
  "Family Court",
  "Federal Circuit and Family Court",
  "Other",
];

export const APPEARANCE_TYPES = [
  "Mention",
  "Contest Mention",
  "Contested Hearing",
  "Plea",
  "Sentence",
  "Bail Application",
  "Committal",
  "Appeal",
  "Trial",
  "Conference",
  "Other",
];

export const GRANT_TYPES = ["No grant yet", "Initial aid", "Ongoing aid", "Extension", "Appeal funding", "Other"];

export const STAGE_OPTIONS = [
  "Advice only",
  "Mention",
  "Bail",
  "Plea",
  "Sentence",
  "Committal",
  "Trial prep",
  "Contested hearing",
  "Trial",
  "Appeal",
  "Other",
];

export const EVENT_TYPES = [
  "instructions_received",
  "aid_application_started",
  "aid_application_lodged",
  "grant_approved",
  "hearing",
  "extension_review",
  "extension_requested",
  "invoice_ready",
  "invoice_sent",
];

export const GRANT_DECISION_OPTIONS = ["Pending", "Granted", "Refused"];
export const POST_APPEARANCE_ACTIONS = [
  "No action",
  "Extension likely needed",
  "New grant likely needed",
  "New single summary grant required for contested hearing",
  "Manual review required",
];

export function createExternalWorkItem() {
  return {
    id: safeRandomUUID(),
    providerType: "",
    providerName: "",
    workRequested: "",
    workDate: "",
    linkedAppearance: "",
    feeType: "",
    providerEngaged: false,
    workCompleted: false,
    coveredByCurrentGrant: "",
    extensionLikelyNeeded: "",
    newGrantLikelyNeeded: "",
    paymentRisk: "",
    notes: "",
  };
}

export function createAppearanceClaim() {
  return {
    id: safeRandomUUID(),
    date: "",
    appearanceType: "",
    customAppearanceType: "",
    outcome: "",
    nextListingType: "",
    grantActionRequired: "",
    feeType: "",
    feeAmount: "",
    feeTable: "",
    feeNote: "",
    invoiceFileName: "",
    claimedUnits: "",
    claimExpected: true,
    claimLodged: false,
    claimPaid: false,
    importSource: "",
    notes: "",
  };
}

export function createPlanner() {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    matterId: safeRandomUUID(),
    createdAt: now,
    updatedAt: now,
    source: {
      type: "manual",
      provider: "",
      externalId: "",
    },
    client: {
      firstName: "",
      lastName: "",
      fullName: "",
      fileNumber: "",
      dateOfBirth: "",
    },
    matter: {
      matterType: "",
      court: "",
      location: "",
      appearanceType: "",
      nextAppearanceDate: "",
      currentAppearanceComplete: false,
      lawyer: "",
      counsel: "No",
      summary: "",
      headCharge: "",
      isCustody: false,
    },
    aid: {
      aidType: "",
      aidTypeList: [],
      guideline: "",
      aidNumber: "",
      extensionNumber: "",
      effectiveDate: "",
      applicationStatus: "Pending",
      uploadedLetterName: "",
      custodyStatus: "",
      documentType: "",
      parsedAlerts: [],
      conflictFlags: [],
      extensions: [],
      approvedGrants: [],
      grantExclusions: [],
      grantConditions: [],
      reconsiderationBy: "",
      contribution: "",
      // Reminders someone has marked done, by reminder key, with when.
      doneReminders: {},
    },
    criminal: {
      pathwayId: "",
      pathwayLabel: "",
      feeTableRef: "",
      feeScheduleRef: "",
      appealCostsCertificateGranted: false,
      counselFeeScheme: "",
      notes: "",
    },
    application: {
      formCompleted: false,
      lodgedInAtlas: false,
      lodgedDate: "",
      decisionReceived: false,
      dateApplied: "",
      decisionResult: "",
    },
    funding: {
      aidApplied: false,
      grantInPlace: false,
      grantType: "",
      vlaReference: "",
      currentStageCovered: "",
      extensionRequested: false,
      extensionDecision: "",
      // Set by "Mark as applied" on an extension reminder, and by an extension approval
      // letter, which covers the rest of the stage it was sought for (listingStage in
      // grantReminders.js). The listing date is kept for approvals recorded before stages.
      extensionRequestedAt: "",
      extensionApprovedForListing: "",
      extensionApprovedForStage: "",
      missingRequirements: [],
    },
    billing: {
      lastInvoiceDate: "",
      billableItems: [],
      holdItems: [],
      extensionItems: [],
      approvalItems: [],
      billingNotes: "",
    },
    finalisation: {
      matterFinalised: false,
      outcomeEnteredInAtlas: false,
      grantClosed: false,
      closureDate: "",
    },
    externalWork: [],
    appearanceClaims: [],
    timeline: [],
    uploadedLetters: [],
    tasks: [],
    folders: {
      "Funding Docs": [],
      "Correspondence": [],
      "Court Orders": [],
      "Medical/Specialist Reports": [],
      "BOE and Disclosure material": [],
      "File Notes": [],
    },
    guidance: {
      status: "missing_info",
      nextAction: "Complete the matter details to assess the matter.",
      reasons: [],
      warnings: [],
    },
    notes: "",
  };
}

export function normalizePlanner(input = {}) {
  const base = createPlanner();
  return {
    ...base,
    ...input,
    source: { ...base.source, ...(input.source || {}) },
    client: { ...base.client, ...(input.client || {}) },
    matter: { ...base.matter, ...(input.matter || {}) },
    aid: {
      ...base.aid,
      ...(input.aid || {}),
      aidTypeList: Array.isArray(input.aid?.aidTypeList) ? input.aid.aidTypeList : base.aid.aidTypeList,
      parsedAlerts: Array.isArray(input.aid?.parsedAlerts) ? input.aid.parsedAlerts : base.aid.parsedAlerts,
      conflictFlags: Array.isArray(input.aid?.conflictFlags) ? input.aid.conflictFlags : base.aid.conflictFlags,
      extensions: Array.isArray(input.aid?.extensions) ? input.aid.extensions : base.aid.extensions,
      approvedGrants: Array.isArray(input.aid?.approvedGrants) ? input.aid.approvedGrants : base.aid.approvedGrants,
      grantExclusions: Array.isArray(input.aid?.grantExclusions) ? input.aid.grantExclusions : base.aid.grantExclusions,
      grantConditions: Array.isArray(input.aid?.grantConditions) ? input.aid.grantConditions : base.aid.grantConditions,
      doneReminders: input.aid?.doneReminders && typeof input.aid.doneReminders === "object" ? input.aid.doneReminders : {},
    },
    criminal: { ...base.criminal, ...(input.criminal || {}) },
    application: { ...base.application, ...(input.application || {}) },
    funding: { ...base.funding, ...(input.funding || {}) },
    billing: { ...base.billing, ...(input.billing || {}) },
    finalisation: { ...base.finalisation, ...(input.finalisation || {}) },
    externalWork: Array.isArray(input.externalWork) ? input.externalWork : [],
    appearanceClaims: Array.isArray(input.appearanceClaims) ? input.appearanceClaims : [],
    timeline: Array.isArray(input.timeline) ? input.timeline : [],
    uploadedLetters: Array.isArray(input.uploadedLetters) ? input.uploadedLetters : [],
    tasks: Array.isArray(input.tasks) ? input.tasks : [],
    folders: input.folders || {
      "Funding Docs": [],
      "Correspondence": [],
      "Court Orders": [],
      "Medical/Specialist Reports": [],
      "BOE and Disclosure material": [],
      "File Notes": [],
    },
    guidance: { ...base.guidance, ...(input.guidance || {}) },
    updatedAt: new Date().toISOString(),
  };
}
